import type { ThemeTokens } from "@klarity/themes";

type FontRef = ThemeTokens["fonts"]["body"];
export type ScreenVars = Record<`--kd-${string}`, string>;

const HEX = /^#[0-9a-fA-F]{6}$/;
const FALLBACK = { body: "Arial, Helvetica, sans-serif", mono: '"Courier New", Courier, monospace' } as const;

const color = (value: string) => (HEX.test(value) ? value : "#000000");
const pt = (n: number) => `${Number.isFinite(n) ? n : 0}pt`;

export function fontStack(ref: FontRef, kind: "body" | "mono"): string {
  const family = ref.family.replace(/["'\\;{}<>]/g, "").trim();
  return family ? `"${family}", ${FALLBACK[kind]}` : FALLBACK[kind];
}

export function screenVars(t: ThemeTokens): ScreenVars {
  const c = t.colors;
  const s = t.sizes;
  const a = t.spacing.after;
  const g = t.spacing.gap;
  return {
    "--kd-primary": color(c.primary),
    "--kd-accent": color(c.accent),
    "--kd-text": color(c.text),
    "--kd-muted": color(c.muted),
    "--kd-code": color(c.code),
    "--kd-border": color(c.border),
    "--kd-stripe": color(c.stripe),
    "--kd-caption": color(c.caption),
    "--kd-note-fill": color(c.noteFill),
    "--kd-warn-fill": color(c.warnFill),
    "--kd-white": color(c.white),
    "--kd-font-body": fontStack(t.fonts.body, "body"),
    "--kd-font-mono": fontStack(t.fonts.mono, "mono"),
    "--kd-size-body": pt(s.body),
    "--kd-size-table": pt(s.table),
    "--kd-size-group": pt(s.group),
    "--kd-size-group-title": pt(s.groupTitle),
    "--kd-size-group-meta": pt(s.groupMeta),
    "--kd-size-code": pt(s.code),
    "--kd-size-small": pt(s.small),
    "--kd-size-title": pt(s.title),
    "--kd-size-h1": pt(s.h1),
    "--kd-size-h2": pt(s.h2),
    "--kd-size-cover-subtitle": pt(s.coverSubtitle),
    "--kd-size-cover-doctype": pt(s.coverDocType),
    "--kd-margin": `${t.page.marginMm}mm`,
    "--kd-after-h1": pt(a.h1),
    "--kd-after-h2": pt(a.h2),
    "--kd-after-p": pt(a.p),
    "--kd-after-kv": pt(a.kv),
    "--kd-after-bullets": pt(a.bullets),
    "--kd-after-steps": pt(a.steps),
    "--kd-after-code": pt(a.code),
    "--kd-before-h1": pt(t.spacing.firstBefore.h1),
    "--kd-before-h2": pt(t.spacing.firstBefore.h2),
    "--kd-gap-after-h1": pt(g.afterH1),
    "--kd-gap-after-h2": pt(g.afterH2),
    "--kd-gap-h1-after-table": pt(g.h1AfterTable),
    "--kd-gap-h1-after-text": pt(g.h1AfterText),
    "--kd-gap-h2-after-table": pt(g.h2AfterTable),
    "--kd-gap-h2-after-text": pt(g.h2AfterText),
    "--kd-gap-p-p": pt(g.pAfterP),
    "--kd-gap-text-text": pt(g.textAfterText),
    "--kd-gap-around-table": pt(g.aroundTable),
    "--kd-cell-pad-v": pt(t.table.cellPadding.v),
    "--kd-cell-pad-h": pt(t.table.cellPadding.h),
  };
}
